import { Box, Button, Flex, Link, ScaleFade, Text } from '@chakra-ui/react';
import { faCookieBite } from '@fortawesome/pro-solid-svg-icons';
import inEU from '@segment/in-eu';
import { selectCurrentUserEmail, selectCurrentUserId, useAuthStore } from '@waitroom/auth';
import { memo, useCallback, useEffect, useState } from 'react';
import { useCookies } from 'react-cookie';
import { useTranslation } from 'react-i18next';
import { useSearchParams } from 'react-router-dom';
import { analyticsService } from '../../../analytics/services';
import { commonConfig } from '../../config';
import Close from '../Button/Close/CloseButton';
import { Icon, IconBox } from '../Icon/Icon';
import Toast from '../Toast/Toast';

export const cookieConsentId = 'CookieConsent';
const expiration = 356; // 1 year
const dismiss = 7; // 7 days
const align = { base: 'start', sm: 'center' };
const isEu = inEU();
const NINETY_DAYS = 1000 * 60 * 60 * 24 * 90;
const IMPACT_RADIUS_COOKIE = 'impactRadius';
const IMPACT_RADIUS_CLICK_ID_PARAM = 'irclickid';

// The name of the component is intentional because ad blocker blocks components on localhost with specific names (eg. cookie)
const CkConsent = memo((): React.ReactElement | null => {
  const { t } = useTranslation();
  const [cookies, setCookie] = useCookies<string>([cookieConsentId]);
  const cookieValue = cookies[cookieConsentId];
  const [responded, setResponded] = useState(cookieValue !== undefined || !isEu);
  const onClick = useCallback(
    (accepted = true) => {
      const expires = new Date();
      expires.setDate(expires.getDate() + (accepted ? expiration : dismiss));
      setCookie(cookieConsentId, accepted, { path: '/', expires });
      setResponded(true);
    },
    [setCookie],
  );

  useEffect(() => {
    if (String(cookieValue) === 'true' || !isEu) analyticsService.init();
  }, [cookieValue]);

  const [params] = useSearchParams();
  const impactRadiusCookie = cookies[IMPACT_RADIUS_COOKIE];
  const impactRadius = impactRadiusCookie || params.get(IMPACT_RADIUS_CLICK_ID_PARAM);
  if (impactRadius) analyticsService.setParams({ impactRadius });

  const userId = useAuthStore(selectCurrentUserId);
  const email = useAuthStore(selectCurrentUserEmail);

  useEffect(() => {
    if (userId && responded) {
      analyticsService.user({ userId, email });
    }
  }, [userId, email, responded]);

  useEffect(() => {
    if (!responded || !impactRadius) return;
    if (!impactRadiusCookie) {
      setCookie(IMPACT_RADIUS_COOKIE, impactRadius, {
        path: '/',
        maxAge: NINETY_DAYS,
      });
    }
  }, [impactRadius, impactRadiusCookie, responded, setCookie]);

  return (
    <Box
      position="fixed"
      w={['80%', '440px']}
      bottom={0}
      left={['10%', '50%']}
      ml={[0, '-220px']}
      zIndex="sticky"
      data-testid="ck-consent"
    >
      <ScaleFade unmountOnExit in={!responded}>
        <Toast
          mx="auto"
          borderRadius="lg"
          opacity="1"
          boxShadow="card"
          background="whiteAlpha.800"
          color="gray.900"
          position="relative"
          zIndex="modal"
          textAlign={'left'}
          data-testid="cookie-consent"
        >
          <Flex w="100%" align={align}>
            <Flex align={align} my={2}>
              <IconBox mr={4} size="2xs" fontSize="lg" bg="black" rounded="full">
                <Icon icon={faCookieBite} color="white" />
              </IconBox>
              <Flex direction={{ base: 'column', sm: 'row' }} align={align}>
                <Text
                  as="span"
                  mr={2}
                  mb={{ base: 2, sm: 0 }}
                  fontSize="sm"
                  fontWeight="bold"
                  lineHeight="base"
                >
                  {t('cookieConsent.description')}
                  <Link
                    ml={2}
                    href={commonConfig.links.privacy}
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    <u>{t('global.moreInfo')}</u>
                  </Link>
                </Text>
                <Button
                  size="xs"
                  colorScheme="gray.900"
                  px={5}
                  mr={2}
                  onClick={() => onClick(true)}
                  data-testid="approve"
                >
                  {t('global.accept')}
                </Button>
              </Flex>
            </Flex>
            <Close
              bg="transparent"
              color="gray.500"
              fontSize="md"
              onClick={() => onClick(false)}
              data-testid="dismiss"
            />
          </Flex>
        </Toast>
      </ScaleFade>
    </Box>
  );
});

export default CkConsent;
